import type { Cell, Color } from "#canvas/types.ts";

/** # 4-way BFS flood fill.
 *
 * Returns the cells matching the original color at `start`; does NOT mutate `data`.\
 * Caller is responsible for applying the new color (single committed change).
 *
 * @param data Canvas data to read from. Must be non-jagged and contain `start`.
 * @param start Cell to flood from.
 * @param newColor Color to fill with. If the same as the original color, no
 *   cells are returned (no-op).
 * @returns Cells matching the original color at `start`, connected 4-way.
 */
export function floodFill(data: Color[][], start: Cell, newColor: Color): Cell[] {
  const startRow = data[start.row];
  if (!startRow) return [];
  const original = startRow[start.col];
  if (original === undefined) return [];
  if (original === newColor) return [];

  const rows = data.length;
  const cols = startRow.length;
  const visited = new Uint8Array(rows * cols);
  const out: Cell[] = [];
  const queue: Cell[] = [start];

  while (queue.length > 0) {
    const cur = queue.shift();
    if (!cur) break;
    const idx = cur.row * cols + cur.col;
    if (visited[idx]) continue;
    visited[idx] = 1;
    const r = data[cur.row];
    if (!r) continue;
    if (r[cur.col] !== original) continue;
    out.push(cur);
    if (cur.row > 0) queue.push({ row: cur.row - 1, col: cur.col });
    if (cur.row < rows - 1) queue.push({ row: cur.row + 1, col: cur.col });
    if (cur.col > 0) queue.push({ row: cur.row, col: cur.col - 1 });
    if (cur.col < cols - 1) queue.push({ row: cur.row, col: cur.col + 1 });
  }

  return out;
}

/** # Bresenham's line algorithm in (row, col) space.
 *
 * Plots a 1-cell-thick line between `a` and `b`, including endpoints.\
 * Uses the same algorithm for all octants, so diagonal lines are jagged.
 * @param a Line start.
 * @param b Line end.
 * @returns Cells along the line from `a` to `b`, including endpoints.
 */
export function bresenhamLine(a: Cell, b: Cell): Cell[] {
  const out: Cell[] = [];
  let r = a.row;
  let c = a.col;
  const dr = Math.abs(b.row - a.row);
  const dc = Math.abs(b.col - a.col);
  const sr = a.row < b.row ? 1 : -1;
  const sc = a.col < b.col ? 1 : -1;
  let err = dc - dr;

  for (;;) {
    out.push({ row: r, col: c });
    if (r === b.row && c === b.col) break;
    const e2 = 2 * err;
    if (e2 > -dr) {
      err -= dr;
      c += sc;
    }
    if (e2 < dc) {
      err += dc;
      r += sr;
    }
  }
  return out;
}

/** # Bounding-box rectangle outline (1-cell-thick) between `a` and `b`.
 *
 * Plots the perimeter of the rectangle defined by the corners `a` and `b`, including endpoints.\
 * @param a First corner.
 * @param b Opposite corner.
 * @returns Cells along the perimeter of the bounding-box rectangle between `a` and `b`, including endpoints.
 */
export function rectOutline(a: Cell, b: Cell): Cell[] {
  const top = Math.min(a.row, b.row);
  const bot = Math.max(a.row, b.row);
  const left = Math.min(a.col, b.col);
  const right = Math.max(a.col, b.col);
  const out: Cell[] = [];
  for (let c = left; c <= right; c++) {
    out.push({ row: top, col: c });
    if (bot !== top) out.push({ row: bot, col: c });
  }
  for (let r = top + 1; r <= bot - 1; r++) {
    out.push({ row: r, col: left });
    if (right !== left) out.push({ row: r, col: right });
  }
  return out;
}

/** # Midpoint ellipse on the bounding box
 * ```
 * [min(a.r,b.r)..max(a.r,b.r)] × [min(a.c,b.c)..max(a.c,b.c)].
 * ```
 * Plots integer cells along the perimeter and deduplicates symmetric overlaps.
 * @see {@link https://en.wikipedia.org/wiki/Midpoint_circle_algorithm | **Wikipedia** | Midpoint circle algorithm}
 * @param a First corner of the bounding box.
 * @param b Opposite corner of the bounding box.
 * @returns Cells along the perimeter of the ellipse inscribed in the bounding box of `a` and `b`, including endpoints.
 */
export function ellipseOutline(a: Cell, b: Cell): Cell[] {
  const top = Math.min(a.row, b.row);
  const bot = Math.max(a.row, b.row);
  const left = Math.min(a.col, b.col);
  const right = Math.max(a.col, b.col);

  const width = right - left;
  const height = bot - top;
  if (width === 0 && height === 0) return [{ row: top, col: left }];

  const cx2 = left + right; // 2 * centerCol
  const cy2 = top + bot; // 2 * centerRow
  const aRad = width / 2;
  const bRad = height / 2;
  if (aRad === 0 || bRad === 0) {
    // degenerate: line segment along the single non-zero axis
    return bresenhamLine({ row: top, col: left }, { row: bot, col: right });
  }
  const a2 = aRad * aRad;
  const b2 = bRad * bRad;

  const seen = new Set<string>();
  const out: Cell[] = [];
  const push = (r: number, c: number): void => {
    const ri = Math.round(r);
    const ci = Math.round(c);
    const key = `${ri}|${ci}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ row: ri, col: ci });
  };

  const plot4 = (dx: number, dy: number): void => {
    push((cy2 + dy * 2) / 2, (cx2 + dx * 2) / 2);
    push((cy2 + dy * 2) / 2, (cx2 - dx * 2) / 2);
    push((cy2 - dy * 2) / 2, (cx2 + dx * 2) / 2);
    push((cy2 - dy * 2) / 2, (cx2 - dx * 2) / 2);
  };

  // Region 1: slope > -1
  let x = 0;
  let y = bRad;
  let d1 = b2 - a2 * bRad + 0.25 * a2;
  let dx = 2 * b2 * x;
  let dy = 2 * a2 * y;
  while (dx < dy) {
    plot4(x, y);
    if (d1 < 0) {
      x++;
      dx += 2 * b2;
      d1 += dx + b2;
    } else {
      x++;
      y--;
      dx += 2 * b2;
      dy -= 2 * a2;
      d1 += dx - dy + b2;
    }
  }

  // Region 2: slope <= -1
  let d2 = b2 * (x + 0.5) * (x + 0.5) + a2 * (y - 1) * (y - 1) - a2 * b2;
  while (y >= 0) {
    plot4(x, y);
    if (d2 > 0) {
      y--;
      dy -= 2 * a2;
      d2 += a2 - dy;
    } else {
      y--;
      x++;
      dx += 2 * b2;
      dy -= 2 * a2;
      d2 += dx - dy + a2;
    }
  }

  return out;
}

/** # Square brush of side `size` centered (as best as possible) on `center`.
 *
 * If `size` is even, the brush is biased down and right, e.g. size 2 covers the center and the cell to its right and below.
 * @param center Cell to center the brush on.
 * @param size Brush size (side length).
 * @returns Cells covered by the brush.
 */
export function brushCells(center: Cell, size: number): Cell[] {
  if (size <= 1) return [center];
  const half = Math.floor(size / 2);
  const out: Cell[] = [];
  for (let dr = -half; dr < size - half; dr++) {
    for (let dc = -half; dc < size - half; dc++) {
      out.push({ row: center.row + dr, col: center.col + dc });
    }
  }
  return out;
}

/** # Fill the bounding-box rectangle between `a` and `b`.
 *
 * Returns all cells in the rectangle defined by the corners `a` and `b`, including endpoints.\
 * @param a First corner.
 * @param b Opposite corner.
 * @returns Cells in the rectangle defined by the corners `a` and `b`, including endpoints.
 */
export function rectFill(a: Cell, b: Cell): Cell[] {
  const top = Math.min(a.row, b.row);
  const bot = Math.max(a.row, b.row);
  const left = Math.min(a.col, b.col);
  const right = Math.max(a.col, b.col);
  const out: Cell[] = [];
  for (let r = top; r <= bot; r++) {
    for (let c = left; c <= right; c++) {
      out.push({ row: r, col: c });
    }
  }
  return out;
}

/** # Fill the ellipse inscribed in the bounding box of `a` and `b`.
 * `{ r, c }` such that `(c - centerCol)² / aRad² + (r - centerRow)² / bRad² <= 1`\
 * |  where `centerRow = (a.r + b.r) / 2, centerCol = (a.c + b.c) / 2` and,\
 * |   `aRad = (b.c - a.c) / 2, bRad = (b.r - a.r) / 2`.
 *
 * If the ellipse degenerates to a line, falls back to `bresenhamLine`
 * to avoid holes.
 *
 * Note that this is a brute-force `O(width×height)` algorithm, which is fine\
 * for small brushes but would be too slow for large ones.
 *
 * If performance becomes an issue, we can optimize by iterating over rows and\
 * calculating column bounds with the ellipse equation instead of checking every cell.
 *
 * @see {@link https://en.wikipedia.org/wiki/Midpoint_circle_algorithm | **Wikipedia** | Midpoint circle algorithm} for the outline version of this algorithm.
 * @param a First corner of the bounding box.
 * @param b Opposite corner of the bounding box.
 * @returns Cells in the ellipse inscribed in the bounding box of `a` and `b`, including endpoints.
 */
export function ellipseFill(a: Cell, b: Cell): Cell[] {
  const top = Math.min(a.row, b.row);
  const bot = Math.max(a.row, b.row);
  const left = Math.min(a.col, b.col);
  const right = Math.max(a.col, b.col);
  if (top === bot && left === right) return [{ row: top, col: left }];
  if (top === bot) return bresenhamLine({ row: top, col: left }, { row: bot, col: right });
  if (left === right) return bresenhamLine({ row: top, col: left }, { row: bot, col: right });

  const cy2 = top + bot;
  const cx2 = left + right;
  const aRad = (right - left) / 2;
  const bRad = (bot - top) / 2;
  const a2 = aRad * aRad;
  const b2 = bRad * bRad;
  const out: Cell[] = [];
  for (let r = top; r <= bot; r++) {
    for (let c = left; c <= right; c++) {
      const dy = (r * 2 - cy2) / 2;
      const dx = (c * 2 - cx2) / 2;
      if ((dx * dx) / a2 + (dy * dy) / b2 <= 1) out.push({ row: r, col: c });
    }
  }
  return out;
}

/** # Thicken a 1-cell trace into a `size`-wide stroke by squaring each cell.
 *
 * For example, a size 3 brush turns each cell into a 3×3 block.\
 * Note that this can produce non-rectangular strokes with holes if the input
 * cells are spaced more than `size` apart.
 *
 * @param cells Cells to thicken. Typically a line or shape outline.
 * @param size Brush size (side length).
 * @returns Thickened cells covering the same general area as the input.
 */
export function dilate(cells: Cell[], size: number): Cell[] {
  if (size <= 1) return cells;
  const seen = new Set<string>();
  const out: Cell[] = [];
  for (const cell of cells) {
    for (const d of brushCells(cell, size)) {
      const key = `${d.row}|${d.col}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(d);
    }
  }
  return out;
}
